import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Button, IconButton, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import * as React from "react";
import { useState } from "react";
import * as Yup from "yup";
import { modalStyle } from "../../../utils/styles";

export default function CategoryModal({ open, onClose, onSubmit, category }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: category ? category.name : "",
      icon_path: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Category name is required"),
      icon_path: Yup.mixed().test(
        "fileType",
        "Unsupported File Format",
        (value) => {
          if (!value) return true;
          const supportedFormats = [
            "image/jpeg",
            "image/png",
            "image/gif",
            "image/jpg",
          ];
          return supportedFormats.includes(value?.type);
        },
      ),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      if (values.icon_path) {
        formData.append("icon_path", values.icon_path);
      }

      onSubmit(formData, category?.id);

      formik.resetForm();
      setSelectedFile(null);
      setThumbnail(null);
      onClose();
    },
  });

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue("icon_path", file);
    setSelectedFile(file);

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnail(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  return (
    <div>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography id="modal-modal-title" variant="h6" component="h2">
            {category ? "Update Category" : "Add New Category"}
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              placeholder="Category Name"
              variant="outlined"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 2 }}
            >
              {selectedFile ? "Change file" : "Select file"}
              <VisuallyHiddenInput type="file" onChange={handleFileChange} />
            </Button>
            {formik.touched.icon_path && formik.errors.icon_path && (
              <Typography color="error">{formik.errors.icon_path}</Typography>
            )}

            {/* Display selected file name */}
            {selectedFile && (
              <Typography sx={{ mt: 1 }}>
                Selected file: {selectedFile.name}
              </Typography>
            )}

            {/* Display thumbnail */}
            {thumbnail && (
              <Box
                component="img"
                src={thumbnail}
                alt="Icon Preview"
                sx={{
                  mt: 2,
                  width: "100px",
                  height: "100px",
                  objectFit: "cover",
                }}
              />
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              {category ? "Update Category" : "Add Category"}
            </Button>
          </form>
        </Box>
      </Modal>
    </div>
  );
}
